class CvBlogInfiniteScroll extends HTMLElement {
  constructor() {
    super();
    this.loading = false;
    this.ended = false;
    this.observer = null;
    this.seenArticleIds = new Set();
  }

  connectedCallback() {
    this.section = this.closest('[data-cv-blog-grid]') || this.closest('.cv-blog-directory');
    this.directory = this.closest('.cv-blog-directory');
    this.grid = this.section?.querySelector('[data-cv-blog-items]');
    this.button = this.querySelector('[data-cv-blog-load-more]');
    this.buttonText = this.querySelector('[data-cv-blog-button-text]');
    this.sentinel = this.querySelector('[data-cv-blog-sentinel]');
    this.live = this.querySelector('[data-cv-blog-live]');
    this.progress = this.querySelector('[data-cv-blog-progress]');
    this.loadedLabel = this.querySelector('[data-cv-blog-loaded]');
    this.nextUrl = this.dataset.nextUrl || '';
    this.total = Number.parseInt(this.dataset.total || '0', 10);
    this.loaded = Number.parseInt(this.dataset.loaded || '0', 10);

    if (!this.grid || !this.button || !this.nextUrl) return;

    this.grid.querySelectorAll('[data-article-id]').forEach((item) => {
      this.seenArticleIds.add(item.dataset.articleId);
    });

    this.directory?.classList.add('is-infinite-enhanced');
    this.button.addEventListener('click', () => this.loadNextPage());

    if (this.dataset.autoLoad === 'true' && 'IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.loadNextPage();
        },
        { rootMargin: '700px 0px', threshold: 0 }
      );
      this.observer.observe(this.sentinel || this.button);
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  async loadNextPage() {
    if (this.loading || this.ended || !this.nextUrl) return;

    this.loading = true;
    this.classList.add('is-loading');
    this.button.disabled = true;
    this.button.setAttribute('aria-busy', 'true');
    if (this.buttonText) this.buttonText.textContent = 'Loading articles…';

    try {
      const response = await fetch(this.nextUrl, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error(`Blog page request failed: ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const nextGrid = doc.querySelector('[data-cv-blog-items]');
      if (!nextGrid) throw new Error('Blog article grid was not found in the next page.');

      const fragment = document.createDocumentFragment();
      let appended = 0;
      nextGrid.querySelectorAll('.blog-post-item').forEach((item) => {
        const id = item.dataset.articleId || '';
        if (id && this.seenArticleIds.has(id)) return;
        if (id) this.seenArticleIds.add(id);
        const clone = item.cloneNode(true);
        clone.classList.remove('cv-blog-featured-item');
        clone.classList.add('cv-blog-new-item');
        fragment.appendChild(clone);
        appended += 1;
      });

      this.grid.appendChild(fragment);
      this.loaded += appended;
      this.updateProgress();

      const nextController = doc.querySelector('blog-infinite-scroll[data-next-url]');
      this.nextUrl = nextController?.dataset.nextUrl || '';

      if (this.live) {
        this.live.textContent = appended > 0
          ? `${appended} more articles loaded. ${this.loaded} of ${this.total} articles shown.`
          : 'No additional articles were found.';
      }

      this.reinitializeThemeFeatures();

      if (!this.nextUrl || appended === 0 || (this.total > 0 && this.loaded >= this.total)) {
        this.finish();
      }
    } catch (error) {
      console.error('[Clothsvilla blog infinite scroll]', error);
      if (this.buttonText) this.buttonText.textContent = 'Try loading again';
      if (this.live) this.live.textContent = 'Articles could not be loaded. Use the button to try again.';
    } finally {
      this.loading = false;
      this.classList.remove('is-loading');
      this.button.disabled = this.ended;
      this.button.removeAttribute('aria-busy');
      if (!this.ended && this.buttonText?.textContent === 'Loading articles…') {
        this.buttonText.textContent = 'Load more articles';
      }
    }
  }

  updateProgress() {
    if (this.loadedLabel) this.loadedLabel.textContent = String(this.loaded);
    if (this.progress) {
      this.progress.value = Math.min(this.loaded, this.total || this.loaded);
      if (this.total) this.progress.max = this.total;
    }
  }

  finish() {
    this.ended = true;
    this.nextUrl = '';
    this.observer?.disconnect();
    if (this.buttonText) this.buttonText.textContent = 'All articles loaded';
    this.button.disabled = true;
    this.button.classList.add('is-complete');
    if (this.live) this.live.textContent = `All ${this.loaded} articles are now shown.`;
  }

  reinitializeThemeFeatures() {
    window.lazyLoad?.update?.();
    window.animateOnScroll?.init?.();
    document.dispatchEvent(new CustomEvent('cv:blog-articles-loaded', {
      detail: { loaded: this.loaded, total: this.total }
    }));
  }
}

if (!customElements.get('blog-infinite-scroll')) {
  customElements.define('blog-infinite-scroll', CvBlogInfiniteScroll);
}
